import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@monorepo/client-common';
import { getTradingSystemName } from '@monorepo/client-types';
import { SystemIcon } from '@monorepo/nova-icons';
import { designTokens, Stack, Typography, useTheme } from '@monorepo/nova-ui';
import {
  Application,
  CartItemEntity,
  CurrencyRates,
  Program,
  ProgramMetadata,
  SupportedCurrencies,
} from '@monorepo/types';
import { formatPriceWithCurrencyAndSign } from '../../utils';

export const CartContentProgram = ({
  allPrograms,
  allCurrencies,
  programsInCart,
  currencyInCart,
  originalPrice,
  project,
}: {
  allPrograms: Program[];
  allCurrencies: CurrencyRates;
  programsInCart: CartItemEntity[];
  currencyInCart: SupportedCurrencies;
  originalPrice: number;
  project: Application;
}) => {
  const { t } = useTranslation('purchase');
  const { palette } = useTheme();

  if (programsInCart.length === 0) {
    return null;
  }

  return (
    <>
      {programsInCart.map((program, index) => {
        const programDetails = allPrograms.find(
          (p: Program) => p._id === program.productId,
        );

        const programMetadata = program.metadata as ProgramMetadata;

        const groupName =
          programDetails?.groupName || programMetadata.productName || 'N/A';
        const buyingPower = programDetails?.config?.buyingPower || 0;
        const tradingSystem = getTradingSystemName(
          programMetadata.tradingSystem,
          'hub',
        );

        return (
          <Stack
            key={`${program.productId}-${index}`}
            gap={1}
            sx={(theme) => ({
              paddingY: theme.spacing(2),
            })}
          >
            <Typography
              variant="caption"
              color={palette.text.third}
              fontSize={designTokens.typography.fontSizes.caption}
              fontWeight={designTokens.typography.fontWeights.regular}
              display="inline-flex"
              gap={1}
              alignItems="center"
            >
              <SystemIcon
                iconName="CheckCircle"
                size={12}
                color={palette.text.third}
              />
              {t('cart.summary.program')}
            </Typography>

            <Stack>
              {/* Row 1: Group name and final price */}
              <Stack direction="row" justifyContent="space-between">
                <Typography
                  variant="body2"
                  fontWeight={designTokens.typography.fontWeights.semiBold}
                  color={palette.text.second}
                  noWrap
                >
                  {t(`groups.${groupName}.title` as never) || 'N/A'}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={designTokens.typography.fontWeights.medium}
                  color={palette.text.main}
                >
                  {formatPriceWithCurrencyAndSign(
                    originalPrice,
                    currencyInCart,
                    allCurrencies,
                  )}
                </Typography>
              </Stack>

              {/* Row 2: Buying power */}
              <Stack>
                <Typography variant="body2" color={palette.text.second}>
                  {`${formatCurrency(buyingPower, { compact: true })} ${t('cart.summary.funding')}`}
                </Typography>
              </Stack>

              {/* Row 3: Platform and trading system */}
              {project === Application.THE5ERS && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color={palette.text.second}>
                    {t('cart.summary.platform')}
                  </Typography>
                  <Typography
                    variant="body2"
                    color={palette.text.main}
                    fontWeight={designTokens.typography.fontWeights.medium}
                  >
                    {tradingSystem || 'N/A'}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
        );
      })}
    </>
  );
};
